// ============= Full file contents =============

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { isValidEmail } from "@/lib/validation";
import { Seo } from "@/components/Seo";

type Mode = "promesse" | "paiement_immediat";
const empty = { lastName: "", firstName: "", phone: "", email: "", amount: "" };

// Déduction fiscale de 66 % pour un don à une association d'intérêt général :
// le don ne "coûte" réellement que 34 % du montant versé.
const TAX_DEDUCTION_RATE = 0.66;

function formatEuros(value: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export default function DonAHV() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(empty);
  const [observatoire, setObservatoire] = useState(false);
  const [exprime, setExprime] = useState(false);
  const [mode, setMode] = useState<Mode>("promesse");
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState<null | "promesse" | "paid">(null);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (params.get("success") === "1" && sessionId) {
      setPopup("paid");
      supabase.functions.invoke("confirm-checkout", { body: { sessionId } }).catch(() => {});
    }
  }, [params]);

  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseInt(form.amount, 10);
    if (!form.lastName.trim() || !form.firstName.trim() || !form.phone.trim() || !isValidEmail(form.email.trim())) {
      toast({ title: "Champs manquants", description: "Merci de remplir nom, prénom, téléphone et un email valide.", variant: "destructive" });
      return;
    }
    if (!amount || amount < 1 || amount > 1000000) {
      toast({ title: "Montant invalide", description: "Merci d'indiquer un montant d'au moins 1 €.", variant: "destructive" });
      return;
    }
    if (!observatoire && !exprime) {
      toast({ title: "Projet", description: "Merci de choisir au moins un projet.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("insert_donation" as never, {
        p_first_name: form.firstName.trim(),
        p_last_name: form.lastName.trim(),
        p_phone: form.phone.trim(),
        p_email: form.email.trim(),
        p_amount: amount,
        p_project_observatoire: observatoire,
        p_project_exprime: exprime,
        p_donation_type: mode,
      } as never);
      if (error) throw error;
      const id = (data as unknown as { id: string }[] | null)?.[0]?.id;
      if (mode === "promesse") {
        setPopup("promesse");
        setForm(empty); setObservatoire(false); setExprime(false);
        return;
      }
      const { data: pay, error: payErr } = await supabase.functions.invoke("create-donation-payment", { body: { donationId: id } });
      if (payErr || !pay?.sessionUrl) throw new Error("payment");
      window.location.href = pay.sessionUrl;
    } catch {
      toast({ title: "Erreur", description: "Une erreur est survenue, merci de réessayer.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const closePopup = () => {
    setPopup(null);
    if (params.get("success")) setParams({}, { replace: true });
  };

  const enteredAmount = parseInt(form.amount, 10);
  const realCost = Number.isFinite(enteredAmount) && enteredAmount >= 1
    ? Math.round(enteredAmount * (1 - TAX_DEDUCTION_RATE) * 100) / 100
    : null;


  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFE34F] to-[#FFF6B8] py-10 px-4">
      <Seo title="Faire un don — Enfants de la République" description="Soutenez les projets Observatoire des Enfants de la République et Exprime la charte." path="/don-AHV2026" />
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg border border-black/10 p-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#001233] mb-6">Soutenez nos projets</h1>


          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Nom *</Label><Input value={form.lastName} onChange={set("lastName")} maxLength={100} /></div>
              <div><Label>Prénom *</Label><Input value={form.firstName} onChange={set("firstName")} maxLength={100} /></div>
              <div><Label>Téléphone *</Label><Input type="tel" value={form.phone} onChange={set("phone")} maxLength={30} /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={set("email")} maxLength={255} /></div>
            </div>
            <div>
              <Label>Montant du don (€) *</Label>
              <Input type="number" min={1} step={1} inputMode="numeric" value={form.amount} onChange={set("amount")} />
              <p className="mt-1.5 text-xs text-[#001233]/70">
                {realCost !== null
                  ? `Votre don ne vous coûtera que ${formatEuros(realCost)} après déduction fiscale. Un CERFA vous sera envoyé.`
                  : "Votre don est déductible de vos impôts. Un CERFA vous sera envoyé."}
              </p>


            </div>

            <div className="space-y-2">
              <Label>Projet(s) soutenu(s) *</Label>
              <label className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer">
                <Checkbox checked={observatoire} onCheckedChange={(v) => setObservatoire(v === true)} />
                <span className="text-[#001233]">Projet Observatoire des Enfants de la République</span>
              </label>
              <label className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer">
                <Checkbox checked={exprime} onCheckedChange={(v) => setExprime(v === true)} />
                <span className="text-[#001233]">Projet Exprime la charte</span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {([["promesse", "Promesse de don", "Je paierai plus tard"], ["paiement_immediat", "Donner maintenant", "Paiement par carte"]] as const).map(([v, t, s]) => (
                <button type="button" key={v} onClick={() => setMode(v)}
                  className={`rounded-lg border-2 p-3 text-left transition ${mode === v ? "border-[#4AA9E6] bg-[#4AA9E6]/10" : "border-black/10"}`}>
                  <div className="font-semibold text-[#001233]">{t}</div>
                  <div className="text-xs text-[#001233]/70">{s}</div>
                </button>
              ))}
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 text-base bg-[#4AA9E6] hover:bg-[#3A99D6] text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "promesse" ? "Enregistrer ma promesse de don" : "Donner maintenant"}
            </Button>
          </form>
        </div>
      </div>

      <Dialog open={popup !== null} onOpenChange={(o) => !o && closePopup()}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#001233] text-xl">Merci pour votre générosité !</DialogTitle>
            <DialogDescription className="text-[#001233]/80 text-base pt-2">
              {popup === "promesse"
                ? "Votre promesse de don est bien enregistrée. Un membre de l'association vous rappellera prochainement."
                : "Votre don a bien été reçu. Vous allez recevoir un email de remerciement avec votre reçu fiscal (CERFA)."}
            </DialogDescription>
          </DialogHeader>
          <Button onClick={closePopup} className="bg-[#4AA9E6] hover:bg-[#3A99D6] text-white">Fermer</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
